import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FaFolderOpen } from "react-icons/fa";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Campaign, OutreachCategory, Tag } from "../../types/outreach";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { TableSkeleton } from "../common/SkeletonLoaders";
import { useTagManagement } from "../../hooks/useTagManagement";

const TAG_FILTER_ALL = "all";
const TAG_FILTER_UNTAGGED = "untagged";

interface CampaignsTabProps {
  campaigns: Campaign[];
  loading: boolean;
  onEdit: (item: any) => void;
  onDelete: (id: string) => void;
}

const CampaignsTab: React.FC<CampaignsTabProps> = ({
  campaigns,
  loading,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();

  // State for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [tagFilter, setTagFilter] = useState<string>(TAG_FILTER_ALL);

  const {
    tags: campaignTags,
    loading: tagOptionsLoading,
    error: tagOptionsError,
  } = useTagManagement({
    category: OutreachCategory.CAMPAIGNS,
    includeGlobal: true,
  });

  const tagsById = useMemo(() => {
    const map = new Map<string, Tag>();
    campaignTags.forEach((tag) => {
      map.set(tag.id, tag);
    });
    return map;
  }, [campaignTags]);

  const tagFilterOptions = useMemo(() => {
    return [...campaignTags].sort((a, b) => a.name.localeCompare(b.name));
  }, [campaignTags]);

  // Handle delete button click
  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete({
      id: campaign.id,
      name: campaign.name,
    });
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (campaignToDelete) {
      onDelete(campaignToDelete.id);
      setDeleteModalOpen(false);
      setCampaignToDelete(null);
    }
  };

  // Handle delete modal close
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setCampaignToDelete(null);
  };

  // Filter to only show saved campaigns
  const savedCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => campaign.saved === true);
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    const activeTag = tagsById.get(tagFilter);

    return savedCampaigns.filter((campaign) => {
      if (
        tagFilter === TAG_FILTER_ALL ||
        tagFilter === "__loading" ||
        tagFilter === "__no_tags"
      )
        return true;

      const campaignTagId =
        campaign.tagId ||
        (typeof campaign.tag !== "string" && campaign.tag
          ? campaign.tag.id
          : null);

      const campaignTagName = (() => {
        if (typeof campaign.tag === "string") {
          return campaign.tag;
        }
        if (campaign.tag && typeof campaign.tag === "object") {
          return campaign.tag.name;
        }
        if (campaignTagId) {
          return tagsById.get(campaignTagId)?.name ?? null;
        }
        return null;
      })();

      if (tagFilter === TAG_FILTER_UNTAGGED) {
        return !campaignTagId && !campaignTagName;
      }

      if (campaignTagId && campaignTagId === tagFilter) {
        return true;
      }

      if (campaignTagId) {
        const resolved = tagsById.get(campaignTagId);
        if (resolved && resolved.id === tagFilter) {
          return true;
        }
      }

      if (activeTag) {
        return (
          (campaignTagName ?? "").toLowerCase() === activeTag.name.toLowerCase()
        );
      }

      return (campaignTagName ?? "").toLowerCase() === tagFilter.toLowerCase();
    });
  }, [savedCampaigns, tagFilter, tagsById]);

  const hasSavedCampaigns = savedCampaigns.length > 0;

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Define columns for the data grid
  const columns: GridColDef[] = [
    { field: "name", headerName: "Campaign Name", flex: 1, minWidth: 200 },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "tag",
      headerName: "Tag",
      flex: 0.6,
      minWidth: 160,
      sortable: false,
      renderCell: (params: any) => {
        const value = params.value as string | Tag | null | undefined;
        const rowTagId =
          (params.row?.tagId as string | undefined | null) ||
          (typeof value !== "string" && value ? value.id : null);
        const resolvedName = (() => {
          if (typeof value === "string") {
            return value;
          }
          if (value && typeof value === "object") {
            return value.name;
          }
          if (rowTagId) {
            return tagsById.get(rowTagId)?.name ?? null;
          }
          return null;
        })();

        return resolvedName ? (
          <Chip
            label={resolvedName}
            color="primary"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip label="No Tag" size="small" variant="outlined" />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 0.7,
      minWidth: 150,
      valueFormatter: (params: any) => formatDate(params.value as string),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.7,
      minWidth: 150,
      valueFormatter: (params: any) => formatDate(params.value as string),
    },
    {
      field: "saved",
      headerName: "Status",
      flex: 0.5,
      minWidth: 120,
      renderCell: (params: any) => (
        <Chip
          label={params.value ? "Saved" : "Draft"}
          color={params.value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 150,
      sortable: false,
      renderCell: (params: any) => (
        <Box>
          <Tooltip title="Edit Campaign">
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              aria-label="edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open Campaign">
            <IconButton
              size="small"
              onClick={() => {
                navigate(`/outreach/campaign/campaigns/${params.row.id}`);
              }}
              aria-label="open"
            >
              <FaFolderOpen size="1.2em" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Campaign">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              aria-label="delete"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {/* Delete Confirmation Modal */}
      {campaignToDelete && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          itemName={campaignToDelete.name}
          onClose={handleDeleteModalClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {loading ? (
        <TableSkeleton rows={10} columns={7} />
      ) : hasSavedCampaigns ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <TextField
              select
              size="small"
              label="Filter by Tag"
              value={tagFilter}
              onChange={(event) => setTagFilter(event.target.value)}
              sx={{ minWidth: 220 }}
              helperText={tagOptionsError?.message}
              error={Boolean(tagOptionsError)}
              SelectProps={{
                displayEmpty: true,
              }}
              disabled={tagOptionsLoading && tagFilterOptions.length === 0}
            >
              <MenuItem value={TAG_FILTER_ALL}>All Tags</MenuItem>
              {tagOptionsLoading && tagFilterOptions.length === 0 && (
                <MenuItem value="__loading" disabled>
                  Loading tags...
                </MenuItem>
              )}
              {tagFilterOptions.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.name}
                </MenuItem>
              ))}
              {!tagOptionsLoading && tagFilterOptions.length === 0 && (
                <MenuItem value="__no_tags" disabled>
                  No tags available
                </MenuItem>
              )}
              <MenuItem value={TAG_FILTER_UNTAGGED}>Untagged</MenuItem>
            </TextField>
          </Box>
          {filteredCampaigns.length === 0 ? (
            <Paper
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No Campaigns Match the Selected Tag
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Try selecting a different tag or reset the filter to view all
                campaigns.
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                height: "calc(100vh - 250px)",
                minWidth: 0,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#48b5bd",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#3da1a9",
                  },
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                  backgroundColor: "#48b5bd",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#3da1a9",
                  },
                },
              }}
            >
              <DataGrid
                rows={filteredCampaigns}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                checkboxSelection={false}
                disableRowSelectionOnClick
                sx={{
                  minWidth: "max-content",
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "pre",
                    wordWrap: "break-word",
                    minWidth: 0,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "background.paper",
                    zIndex: 1,
                  },
                  "@media (max-width: 768px)": {
                    "& .MuiDataGrid-cell": {
                      fontSize: "0.875rem",
                      padding: "4px 8px",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "0.875rem",
                      padding: "4px 8px",
                    },
                  },
                  "@media (max-width: 480px)": {
                    "& .MuiDataGrid-cell": {
                      fontSize: "0.75rem",
                      padding: "2px 4px",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "0.75rem",
                      padding: "2px 4px",
                    },
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#48b5bd",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#3da1a9",
                      },
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      borderRadius: "4px",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                      backgroundColor: "#48b5bd",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#3da1a9",
                      },
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Paper
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Campaigns Found
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Create a new campaign by clicking the "Add Campaign" button above.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CampaignsTab;
